import {
  Icon,
  PlatformSidebar,
  PlatformSidebarItem,
  usePlatformConfig,
} from "@aurem/components";
import { BadgeProps, useBreakpoints } from "@aurem/ui";
import ROUTES from "const/routes";
import { useHasPlatformRole, useUserPermissions } from "graphql/user";
import { useBasePath } from "hooks/useBasePath";
import {
  ActivityIcon,
  CalculatorIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  Landmark,
  LifeBuoyIcon,
  Users,
} from "lucide-react";
import { useEnvironmentConfig } from "pages/Controls/hooks/environment.hooks";
import { useChatwoot } from "providers/ChatwootProvider";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import { ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { PermissionEnum } from "types";
import { CONTROLS_PERMISSIONS, REQUESTS_PERMISSIONS } from "./constants";
import { ControlsDropdown } from "./ControlsDropdown";
import { ShowMoreDropdown } from "./ShowMoreDropdown";
import { useTasksCountPolling } from "./useTasksCountPolling";

export type SidebarItemData = {
  id: string;
  to: string;
  icon: ReactElement;
  label: string;
  badgeProps?: Omit<BadgeProps, "children">;
};

const THRESHOLD = 5;
const EXCLUDED_ROUTES = ["/disclaimers"] as const;

type Props = {
  open?: boolean;
  onClose?: () => void;
  className?: string;
};

export const LayoutOuterSidebar = (props: Props) => {
  const { pathname } = useLocation();
  const { config, loading } = useEnvironmentConfig();
  const isPlatformAdmin = useHasPlatformRole();

  const isOnboardingMode =
    config?.firstLogin && pathname.includes("/controls") && isPlatformAdmin;

  const shouldHide = EXCLUDED_ROUTES.some((route) => pathname.includes(route));

  if (loading || !isPlatformAdmin || isOnboardingMode || shouldHide)
    return null;

  return <LayoutOuterSidebarView {...props} />;
};

const LayoutOuterSidebarView = (props: Props) => {
  const { t } = useI18n();
  const { path } = useBasePath();
  const { isMobile } = useBreakpoints();
  const config = usePlatformConfig();
  const { userPermissions, permissionsContain } = useUserPermissions();
  const isTasksEnabled = useFeature("tasks_feature");
  const isActivityEnabled = useFeature("activity_feature");
  const { count: tasksCount } = useTasksCountPolling({
    enabled: isTasksEnabled,
  });
  const chatwoot = useChatwoot();

  const logoAlt = config?.name
    ? t("apps.admin.common.hubName", config.name)
    : t("apps.admin.common.hubName", "Aurem");

  const itemsData: SidebarItemData[] = [];
  const footer = [];

  if (permissionsContain(userPermissions, [PermissionEnum.ListCompany])) {
    itemsData.push({
      id: "companies",
      to: path(ROUTES.COMPANIES),
      icon: <Icon.Companies size={20} />,
      label: t("apps.admin.navBar.companies"),
    });
  }

  if (isTasksEnabled) {
    itemsData.push({
      id: "tasks",
      to: path(ROUTES.TASKS),
      icon: <ClipboardCheckIcon size={20} />,
      label: t("apps.admin.navBar.tasks"),
      badgeProps: {
        content: tasksCount,
        offset: {
          top: 12,
          end: 25,
        },
      },
    });
  }

  if (permissionsContain(userPermissions, REQUESTS_PERMISSIONS)) {
    itemsData.push({
      id: "requests",
      to: path(ROUTES.REQUESTS),
      icon: <ClipboardListIcon size={20} />,
      label: t("apps.admin.navBar.requests"),
    });
  }

  const showControls = permissionsContain(
    userPermissions,
    CONTROLS_PERMISSIONS,
  );

  if (showControls) {
    footer.push(<ControlsDropdown key="controls" />);
  }

  if (chatwoot.enabled) {
    footer.push(
      <PlatformSidebarItem
        key="support"
        icon={<LifeBuoyIcon size={20} />}
        label={t("apps.admin.navBar.support")}
        onClick={chatwoot.show}
      />,
    );
  }

  if (
    permissionsContain(userPermissions, [
      PermissionEnum.ViewCompanyPayment,
      PermissionEnum.ViewWithdrawal,
    ])
  ) {
    itemsData.push({
      id: "payments",
      to: path(ROUTES.PAYMENTS),
      icon: <Icon.Payment size={20} />,
      label: t("apps.admin.navBar.payments"),
    });
  }

  if (
    isActivityEnabled &&
    permissionsContain(userPermissions, [PermissionEnum.ViewCompanyPayment])
  ) {
    itemsData.push({
      id: "activity",
      to: path(ROUTES.ACTIVITY),
      icon: <ActivityIcon size={20} />,
      label: t("apps.admin.navBar.activity"),
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ViewMarketOrder])) {
    itemsData.push({
      id: "market-orders",
      to: path(ROUTES.MARKET_ORDERS),
      icon: <Landmark size={20} />,
      label: t("apps.admin.navBar.marketOrders"),
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ViewFund])) {
    itemsData.push({
      id: "holdings",
      to: path(ROUTES.HOLDINGS),
      icon: <CalculatorIcon size={20} />,
      label: t("apps.admin.navBar.holdings"),
    });
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ViewUser])) {
    itemsData.push({
      id: "users",
      to: path(ROUTES.USERS),
      icon: <Users size={20} />,
      label: t("apps.admin.navBar.users"),
    });
  }

  const visibleCount =
    itemsData.length > THRESHOLD ? THRESHOLD - 1 : itemsData.length;
  const visibleItems = itemsData.slice(0, visibleCount);
  const overflowItems = itemsData.slice(visibleCount);

  return (
    <PlatformSidebar
      logoAlt={logoAlt}
      logoUrl={config.logoUrlSymbol}
      footer={footer}
      open={props.open}
      onClose={props.onClose}
      className={props.className}
    >
      {visibleItems.map((item) => (
        <PlatformSidebarItem
          key={item.id}
          to={item.to}
          icon={item.icon}
          label={item.label}
          badgeProps={item.badgeProps}
          onClick={props.onClose}
        />
      ))}
      {overflowItems.length > 0 && (
        <ShowMoreDropdown options={overflowItems} onClose={props.onClose} />
      )}
    </PlatformSidebar>
  );
};
